import { DEFAULT_PER_METAL_PRECISION } from '@/util/refinery/constants'

const DEFAULT_METAL_SYMBOL_TO_NAME_MAP = {
  'AU': 'Gold',
  'AG': 'Silver',
  'PT': 'Platinum',
  'PD': 'Palladium'
}

/**
 * Get decimal precision for a metal symbol.
 * 
 * Checks store settings first, falls back to defaults: AU/PT/PD=3, AG=1, unknown=4.
 * Input is normalized (case-insensitive, X prefix stripped for ISO codes like XAU).
 * 
 * @param {string} metalSymbol - Metal symbol (AU, AG, PT, PD, or ISO like XAU)
 * @returns {number} Number of decimal places
 */
function getMetalPrecision(metalSymbol) {
  const symbol = String(metalSymbol || '').toUpperCase().replace(/^X/, '')
  try {
    const precision = require('@/store').default?.getters?.refinerySettings?.perMetalWeightPrecision?.[symbol]
    if (typeof precision === 'number') return precision
  } catch { /* store unavailable */ }
  return DEFAULT_PER_METAL_PRECISION[symbol] ?? 4
}

/**
 * Round weight to per-metal precision
 * @param {number} weight - Weight to round
 * @param {string} metalSymbol - Metal symbol (AU, AG, PT, PD)
 * @returns {number} Rounded weight
 */
function roundWeightForMetal(weight, metalSymbol) {
  const precision = getMetalPrecision(metalSymbol)
  const factor = Math.pow(10, precision)
  return Math.round(weight * factor) / factor
}

export function getSpotPricesForMetal(metalSymbol, liveSpotPrices, metalSymbolToNameMap = DEFAULT_METAL_SYMBOL_TO_NAME_MAP) {
  if (!metalSymbol || !liveSpotPrices) return null
  const metalName = metalSymbolToNameMap[metalSymbol];
  if (!metalName || !liveSpotPrices[metalName]) {
    return null
  }
  return liveSpotPrices[metalName]
}

export function computeTransactionValues(transaction, spotPrices) {
  const { type, direction, metalSymbol, metalWeight } = transaction
  const metalSpotPrices = getSpotPricesForMetal(metalSymbol, spotPrices);
  const priceType = getSpotPriceTypeForTransaction(type, direction)
  const spotPrice = metalSpotPrices?.[priceType]

  let computedValues = { ...transaction };

  // Round metalWeight to per-metal precision (AU/PT/PD=3, AG=1)
  const roundedWeight = metalSymbol ? roundWeightForMetal(metalWeight, metalSymbol) : metalWeight

  switch (type) {
  case 'cash_advance':
  case 'metal_receipt':
  case 'cash_settlement':
  case 'metal_settlement':
  case 'metal_delivery':
  case 'spot_lock':
    computedValues.currencyValue = Math.round(roundedWeight * spotPrice * 10000) / 10000;
    break;
  case 'cash_out':
    if (roundedWeight && roundedWeight > 0) {
      computedValues.currencyValue = Math.round(roundedWeight * spotPrice * 10000) / 10000;
    }
    break;
  }

  // Convert ozt back to TOZ
  if (computedValues.metalUnits === 'ozt') computedValues.metalUnits = 'TOZ';

  return {
    ...computedValues,
    currencyValue: Number(computedValues.currencyValue),
    metalWeight: Number(roundedWeight),
  };
}

export function getSpotPriceTypeForTransaction(type, direction) {
  if (type === 'spot_lock') {
    return direction === 'sell' ? 'bid' : 'ask';
  }
  if (['cash_out', 'cash_advance', 'metal_receipt', 'cash_settlement', 'metal_settlement'].includes(type)) {
    return 'bid';
  }
  if (['metal_delivery'].includes(type)) {
    return 'ask';
  }
  if (['internal_transfer', 'transfer', 'spot_lock_fulfillment', 'metal_out', 'external_deposit'].includes(type)) {
    return null;
  }
  return null;
}

export function normalizeTransactionPayload(payload) {
  const updated = { ...payload };

  if (updated.type === 'cash_advance') {
    updated.metalWeight = 0;
  }

  if (updated.type === 'cash_out') {
    if (!updated.metalWeight) {
      delete updated.metalSymbol;
      delete updated.metalWeight;
      delete updated.metalUnits;
    }
  }

  if ([
    'metal_settlement',
    'metal_out',
    'metal_receipt'
  ].includes(updated.type)) {
    updated.currencyValue = 0;
  }

  if (!updated.lockFulfillmentMetalWeight) {
    delete updated.lockFulfillmentMetalWeight;
  }
  if (!updated.lockFulfillmentType) {
    delete updated.lockFulfillmentType;
  }

  return updated;
}

export function formatBalance (val, unitOrSymbol) {
  const num = Number(val)
  if (!isFinite(num)) return String(val)

  // Currency balances (USD)
  if (String(unitOrSymbol).toUpperCase() === 'USD') {
    const isNegative = num < 0
    const absVal = Math.abs(num)
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absVal)
    return `${isNegative ? '-' : ''}$${formatted}`
  }

  // Metal balances: use shared precision logic
  const symbol = String(unitOrSymbol || '').toUpperCase()
  if (['AU', 'AG', 'PT', 'PD'].includes(symbol)) {
    return `${num.toFixed(getMetalPrecision(symbol))} ozt`
  }

  // Generic units (e.g., 'ozt', 'TOZ') fallback to 4 decimals
  return unitOrSymbol ? `${num.toFixed(4)} ${unitOrSymbol}` : num.toFixed(4)
}

export function calculateRunningBalances(transactions, ledgerAccounts, selectedCompanyId) {
  const balances = {};

  const accountMap = ledgerAccounts.reduce((map, acct) => {
    map[acct.id] = acct.accountCode;
    return map;
  }, {});

  const sortedTxns = [...transactions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const transactionsWithBalances = sortedTxns.map(txn => {
    const updatedTxn = { ...txn };

    // Determine Role
    if (txn.companyId === selectedCompanyId) {
      updatedTxn.role = 'Initiator';
    } else if (txn.counterpartyCompanyId === selectedCompanyId) {
      updatedTxn.role = 'Recipient';
    } else if (txn.counterpartyCompanyId === null) {
      updatedTxn.role = 'System';
    } else {
      updatedTxn.role = 'Other';
    }

    if (txn.status === 'cancelled') {
      updatedTxn.runningMetalBalance = '--';
      updatedTxn.runningCurrencyBalance = '--';
    } else {
      const associatedEntries = updatedTxn.journalEntries || [];

      associatedEntries.forEach(entry => {
        const lines = Array.isArray(entry.lines) ? entry.lines : [];
        lines.forEach(line => {
          const accountCode = accountMap[line.ledgerAccountId];

          if (accountCode === 'cash_balance' || accountCode === 'metal_balance') {
            const metalDelta = (line.debitMetalWeight || 0) - (line.creditMetalWeight || 0);
            const currencyDelta = (line.debitCurrencyValue || 0) - (line.creditCurrencyValue || 0);

            const metalSymbol = txn.metalSymbol || '--';
            const currencyType = txn.currencyType || '--';
            const key = `${metalSymbol}-${currencyType}`;

            if (!balances[key]) {
              balances[key] = { metalWeight: 0, currencyValue: 0 };
            }

            balances[key].metalWeight += metalDelta;
            balances[key].currencyValue += currencyDelta;
          }
        });
      });

      const keyForTxn = `${txn.metalSymbol || '--'}-${txn.currencyType || '--'}`;
      if (balances[keyForTxn]) {
        updatedTxn.runningMetalBalance = Math.round(balances[keyForTxn].metalWeight * 10000) / 10000;
        updatedTxn.runningCurrencyBalance = Math.round(balances[keyForTxn].currencyValue * 10000) / 10000;
      } else {
        updatedTxn.runningMetalBalance = 0;
        updatedTxn.runningCurrencyBalance = 0;
      }
    }
    return updatedTxn;
  });

  return transactionsWithBalances.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function extractSpotPriceFromSnapshot(metalTransaction) {
  if (!metalTransaction || !metalTransaction.priceSnapshot || !metalTransaction.metalSymbol) {
    return undefined
  }

  const { type, direction, priceSnapshot, metalSymbol } = metalTransaction
  const priceType = getSpotPriceTypeForTransaction(type, direction)

  if (priceSnapshot.metalSymbol !== metalSymbol) {
    console.warn(`Spot price snapshot metal (${priceSnapshot.metalSymbol}) does not match transaction metal (${metalSymbol}).`);
  }

  return priceSnapshot?.[priceType]
}

/** Match API TransactionPriceSnapshot.getLockedPriceForDirection for display. */
export function extractLockedSpotPriceFromSnapshot({direction, priceSnapshot} = {}) {
  if (!priceSnapshot) return undefined
  if (direction === 'sell') return parseFloat(priceSnapshot.bidWithBuffer ?? priceSnapshot.bid)
  if (direction === 'buy') return parseFloat(priceSnapshot.askWithBuffer ?? priceSnapshot.ask)
  return undefined
}

export function extractDisplaySpotPrice(metalTransaction) {
  if (!metalTransaction) return undefined
  if (metalTransaction.lockedSpotPrice != null) {
    return metalTransaction.lockedSpotPrice
  }
  if (metalTransaction.type === 'spot_lock') {
    return extractLockedSpotPriceFromSnapshot(metalTransaction)
  }
  return undefined
}

// Named exports for functions not already exported inline
export { getMetalPrecision, roundWeightForMetal }

export default {
  calculateRunningBalances,
  computeTransactionValues,
  extractSpotPriceFromSnapshot,
  extractLockedSpotPriceFromSnapshot,
  extractDisplaySpotPrice,
  formatBalance,
  getMetalPrecision,
  getSpotPricesForMetal,
  getSpotPriceTypeForTransaction,
  normalizeTransactionPayload,
  roundWeightForMetal
}